import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AcademicCapIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  EyeIcon,
  MinusCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { AppPage, BTN_PRIMARY, BTN_SECONDARY, CARD, Empty, Eyebrow, HeroPanel } from '../components/UI/AppUI';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.air-brief.com';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('atpl_access_token')}` });

const formatTime = (seconds) => {
  if (!seconds) return '0 min';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (!m) return `${s}s`;
  return s ? `${m}m ${s}s` : `${m} min`;
};

// Score ring with the pass mark underneath
const ScoreRing = ({ value, passMark, tone }) => {
  const r = 54;
  const c = 2 * Math.PI * r;
  return (
    <span className="relative flex h-40 w-40 flex-shrink-0 items-center justify-center">
      <svg viewBox="0 0 128 128" className="absolute inset-0 h-full w-full" aria-hidden="true">
        <g transform="rotate(-90 64 64)">
          <circle cx="64" cy="64" r={r} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="10" />
          <circle cx="64" cy="64" r={r} fill="none" strokeWidth="10" strokeLinecap="round" className={tone}
            strokeDasharray={c} strokeDashoffset={c * (1 - Math.min(100, value) / 100)} style={{ transition: 'stroke-dashoffset 1s ease-out' }} />
        </g>
      </svg>
      <span className="relative text-center">
        <span className="block text-4xl font-semibold tabular-nums text-white">{Math.round(value)}%</span>
        <span className="block text-xs text-gray-400">pass mark {passMark}%</span>
      </span>
    </span>
  );
};

const Results2 = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [examResults, setExamResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startingRetry, setStartingRetry] = useState(false);

  useEffect(() => {
    if (!examId) return;
    (async () => {
      try {
        const response = await fetch(`${API_URL}/api/exams/sessions/${examId}/`, { headers: authHeaders() });
        if (response.ok) setExamResults(await response.json());
        else setError('Failed to load exam results');
      } catch {
        setError('Network error occurred');
      } finally {
        setLoading(false);
      }
    })();
  }, [examId]);

  // Start a practice exam with exactly the questions answered wrong in this one
  const handlePracticeWrong = async (questionIds) => {
    setStartingRetry(true);
    try {
      const response = await fetch(`${API_URL}/api/exams/sessions/create/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          subject: examResults.subject.id,
          mode: 'practice',
          total_questions: questionIds.length,
          question_ids: questionIds,
          title: `${examResults.subject.name} - Mistakes review`,
        }),
      });
      const data = await response.json();
      if (response.ok) navigate(`/exam/${data.exam_session_id || data.id}`);
      else setError(data.error || 'Could not start the practice exam');
    } catch {
      setError('Network error occurred');
    } finally {
      setStartingRetry(false);
    }
  };

  if (loading) {
    return (
      <AppPage>
        <div className="h-64 animate-pulse rounded-3xl bg-slate-200 dark:bg-white/5" />
        <div className="mt-6 h-40 animate-pulse rounded-2xl bg-slate-200 dark:bg-white/5" />
      </AppPage>
    );
  }

  if (error || !examResults) {
    return (
      <AppPage>
        <div className={`${CARD} mx-auto max-w-lg`}>
          <Empty icon={error ? XCircleIcon : DocumentTextIcon} title={error ? "We couldn't load these results" : 'No results found'}
            action={<Link to="/tests" className={BTN_PRIMARY}>Go to My tests</Link>}>
            {error || 'These test results could not be found.'}
          </Empty>
        </div>
      </AppPage>
    );
  }

  const {
    score_percentage, questions_correct = 0, questions_incorrect = 0, questions_skipped = 0,
    passed, time_spent_seconds, completed_at, subject, mode, passing_score, exam_questions = [],
  } = examResults;

  const totalQuestions = questions_correct + questions_incorrect + questions_skipped;
  const isPractice = mode === 'practice';
  const passMark = passing_score || 75;
  const score = score_percentage || 0;
  const verdict = isPractice ? (passed ? 'On track' : 'Keep practising') : (passed ? 'Passed' : 'Not passed');
  const tone = passed ? 'stroke-emerald-400' : isPractice ? 'stroke-amber-400' : 'stroke-rose-400';
  const verdictCls = passed ? 'bg-emerald-500/15 text-emerald-300 ring-emerald-400/30' : isPractice ? 'bg-amber-500/15 text-amber-300 ring-amber-400/30' : 'bg-rose-500/15 text-rose-300 ring-rose-400/30';
  const avgPerQuestion = totalQuestions && time_spent_seconds ? Math.round(time_spent_seconds / totalQuestions) : null;

  const wrongQuestionIds = exam_questions
    .filter((q) => q.is_correct === false && (q.selected_answer || q.user_input_answer))
    .map((q) => q.question?.id)
    .filter(Boolean);

  // Correct / total per chapter, weakest first
  const chapterStats = Object.values(exam_questions.reduce((acc, q) => {
    const name = q.question?.chapter_name || 'Other';
    acc[name] = acc[name] || { name, correct: 0, total: 0 };
    acc[name].total += 1;
    if (q.is_correct) acc[name].correct += 1;
    return acc;
  }, {})).sort((a, b) => a.correct / a.total - b.correct / b.total);

  return (
    <AppPage>
      <HeroPanel image={null}>
        <Link to={subject?.id ? `/subjects/${subject.id}` : '/dashboard'} className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-300 hover:text-white">
          <ArrowLeftIcon className="h-4 w-4" />{subject?.name || 'Back'}
        </Link>
        <div className="mt-6 flex flex-col items-center gap-8 text-center md:flex-row md:text-left">
          <ScoreRing value={score} passMark={passMark} tone={tone} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center justify-center gap-2 md:justify-start">
              <span className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold ring-1 ${verdictCls}`}>{verdict}</span>
              <span className="text-sm text-gray-400">{isPractice ? 'Practice' : 'Real test'}{completed_at ? ` · ${new Date(completed_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}</span>
            </div>
            <h1 className="mt-3 text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              {questions_correct} of {totalQuestions} correct
            </h1>
            <p className="mt-2 text-gray-300">
              {passed
                ? (isPractice ? 'Above the pass mark. Keep it up.' : 'Well done, that would pass the real exam.')
                : `You need ${passMark}% to pass. ${wrongQuestionIds.length ? 'Practising your mistakes is the quickest way there.' : ''}`}
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center md:justify-start">
              {wrongQuestionIds.length > 0 && (
                <button type="button" onClick={() => handlePracticeWrong(wrongQuestionIds)} disabled={startingRetry} className={BTN_PRIMARY}>
                  <ArrowPathIcon className={`h-4 w-4 ${startingRetry ? 'animate-spin' : ''}`} />
                  {startingRetry ? 'Starting…' : `Practise the ${wrongQuestionIds.length} I got wrong`}
                </button>
              )}
              <Link to={`/exam/${examId}/review`} className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/25 bg-white/10 px-5 py-2.5 text-sm font-semibold text-white backdrop-blur hover:bg-white/20">
                <EyeIcon className="h-4 w-4" />Review answers
              </Link>
            </div>
          </div>
        </div>
      </HeroPanel>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[
          { icon: CheckCircleIcon, tone: 'text-emerald-500', label: 'Correct', value: questions_correct },
          { icon: XCircleIcon, tone: 'text-rose-500', label: 'Wrong', value: questions_incorrect },
          { icon: MinusCircleIcon, tone: 'text-slate-400', label: 'Skipped', value: questions_skipped },
          { icon: ClockIcon, tone: 'text-primary-500', label: 'Time', value: formatTime(time_spent_seconds), hint: avgPerQuestion ? `${avgPerQuestion}s per question` : null },
        ].map(({ icon: Icon, tone: t, label, value, hint }) => (
          <div key={label} className={`${CARD} p-5`}>
            <span className="flex items-center gap-2 text-sm text-slate-500 dark:text-gray-400"><Icon className={`h-4 w-4 ${t}`} />{label}</span>
            <p className="mt-2 text-3xl font-semibold tracking-tight text-slate-900 tabular-nums dark:text-white">{value}</p>
            {hint && <p className="mt-1 text-xs text-slate-500 dark:text-gray-500">{hint}</p>}
          </div>
        ))}
      </div>

      {chapterStats.length > 1 && (
        <section className={`${CARD} mt-6 p-5 sm:p-6`}>
          <Eyebrow>Breakdown</Eyebrow>
          <h2 className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">By chapter, weakest first</h2>
          <ul className="mt-5 space-y-4">
            {chapterStats.map((c) => {
              const p = Math.round((c.correct / c.total) * 100);
              return (
                <li key={c.name}>
                  <div className="mb-1.5 flex justify-between gap-3 text-sm">
                    <span className="truncate text-slate-700 dark:text-gray-300">{c.name}</span>
                    <span className="flex-shrink-0 tabular-nums text-slate-500 dark:text-gray-400">{c.correct}/{c.total} · <span className="font-semibold text-slate-900 dark:text-white">{p}%</span></span>
                  </div>
                  <span className="relative block h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
                    <span className={`block h-full rounded-full ${p >= passMark ? 'bg-emerald-500' : p >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{ width: `${p}%` }} />
                    <span className="absolute inset-y-0 w-px bg-slate-500/60 dark:bg-white/40" style={{ left: `${passMark}%` }} aria-hidden="true" />
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link to={subject?.id ? `/subjects/${subject.id}` : '/dashboard'} className={BTN_SECONDARY}><AcademicCapIcon className="h-4 w-4" />Back to {subject?.name ? subject.name.replace(/^(DLR|ATPL)\s*-\s*/i, '') : 'the subject'}</Link>
        <Link to="/tests" className={BTN_SECONDARY}>All my tests</Link>
      </div>
    </AppPage>
  );
};

export default Results2;
